import { Extension } from '@tiptap/core';
import { Table, TableView } from '@tiptap/extension-table';
import type { DOMOutputSpec, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';

/**
 * Table markup the schema has no node for.
 *
 * prosemirror-tables knows rows and cells, so <thead> and <tbody> are flattened on load and a
 * table that repeated its header on every printed page stopped doing so after one edit. Each
 * row remembers the section it came from; the save step regroups rows into <thead>/<tfoot>
 * from that (see restoreTableSections). The presentational attributes writers still put on
 * tables and cells are kept for the same reason: dropping them changed the spacing.
 */
const ROW_SECTIONS: Record<string, string> = { THEAD: 'head', TFOOT: 'foot' };

export const TABLE_SECTION_ATTRIBUTE = 'data-table-section';

const passthrough = (name: string) => ({
  default: null as string | null,
  parseHTML: (element: HTMLElement) => element.getAttribute(name),
  renderHTML: (attributes: Record<string, string | null>) =>
    attributes[name] ? { [name]: attributes[name] } : {}
});

const passthroughAll = (names: string[]) =>
  Object.fromEntries(names.map((name) => [name, passthrough(name)]));

type Column = Record<string, string>;

const withColumns = (spec: DOMOutputSpec, columns: Column[]): DOMOutputSpec => {
  if (!Array.isArray(spec)) return spec;
  if (spec[0] === 'colgroup')
    return ['colgroup', ...columns.map((attrs) => ['col', attrs] as DOMOutputSpec)];
  return [
    spec[0],
    ...spec
      .slice(1)
      .map((part) =>
        Array.isArray(part) ? withColumns([part[0], ...part.slice(1)], columns) : part
      )
  ];
};

const importedColumns = (node: ProseMirrorNode): Column[] | null => {
  const columns = node.attrs.columns as Column[] | null;
  if (
    !columns ||
    columns.reduce((n, col) => n + Math.max(1, Number(col.span) || 1), 0) !==
      TableMap.get(node).width
  )
    return null;
  let resized = false;
  node.forEach((row) =>
    row.forEach((cell) => {
      if (cell.attrs.colwidth?.some((width: number) => width > 0)) resized = true;
    })
  );
  return resized ? null : columns;
};

class DocumentTableView extends TableView {
  constructor(...args: ConstructorParameters<typeof TableView>) {
    super(...args);
    this.applyColumns();
  }

  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) return false;
    this.colgroup.replaceChildren();
    super.update(node);
    this.applyColumns();
    return true;
  }

  private applyColumns() {
    const columns = importedColumns(this.node);
    if (!columns) return;
    this.colgroup.replaceChildren(
      ...columns.map((attrs) => {
        const col = document.createElement('col');
        for (const [name, value] of Object.entries(attrs)) col.setAttribute(name, value);
        return col;
      })
    );
  }
}

/** Preserve the renderer's column sizes, which ProseMirror's cell schema cannot express as percentages. */
export const DocumentTable = Table.extend({
  addOptions() {
    return { ...this.parent!(), View: DocumentTableView };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      columns: {
        default: null,
        rendered: false,
        parseHTML: (element: HTMLElement) => {
          const columns = Array.from(element.querySelectorAll(':scope > colgroup > col'), (col) =>
            Object.fromEntries(
              ['style', 'width', 'span', 'class', 'id']
                .filter((name) => col.hasAttribute(name))
                .map((name) => [name, col.getAttribute(name)!])
            )
          );
          return columns.length ? columns : null;
        }
      }
    };
  },

  renderHTML(props) {
    const spec = this.parent!(props);
    const columns = importedColumns(props.node);
    // Explicit cell resizing wins; a changed column count uses the table editor's new grid.
    return columns ? withColumns(spec, columns) : spec;
  }
});

export const TableMarkupExtension = Extension.create({
  name: 'tableMarkup',

  addGlobalAttributes() {
    return [
      {
        types: ['tableRow'],
        attributes: {
          section: {
            default: null as string | null,
            parseHTML: (element: HTMLElement) =>
              ROW_SECTIONS[element.parentElement?.tagName ?? ''] ?? null,
            renderHTML: (attributes: Record<string, string | null>) =>
              attributes.section ? { [TABLE_SECTION_ATTRIBUTE]: attributes.section } : {}
          }
        }
      },
      {
        types: ['table'],
        attributes: passthroughAll(['border', 'cellpadding', 'cellspacing', 'width'])
      },
      { types: ['tableCell', 'tableHeader'], attributes: passthroughAll(['width', 'height']) }
    ];
  }
});
